import { useSyncExternalStore } from "react";

/**
 * Deferred loader for heavy third-party embeds (LeadConnector form + chat
 * widget, review widget). Everything stays out of the critical path until the
 * first genuine user interaction, then loads once.
 *
 * The gate listens for pointer / touch / keyboard / wheel input only — never
 * `scroll`, because automated audits (Lighthouse) programmatically scroll the
 * page and we do not want that to pull in ~3 MB of third-party resources.
 */
const INTERACTION_EVENTS = ["pointerdown", "pointermove", "touchstart", "keydown", "wheel"] as const;

let activated = false;
let armed = false;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((l) => l());

const loadEmbedScripts = () => {
  if (typeof document === "undefined") return;
  // LeadConnector form embed script (handles iframe auto-resize + messaging)
  if (!document.querySelector('script[data-lc="form-embed"]')) {
    const s = document.createElement("script");
    s.src = "https://link.systemic-digital.net/js/form_embed.js";
    s.async = true;
    s.setAttribute("data-lc", "form-embed");
    document.body.appendChild(s);
  }
  // LeadConnector chat widget loader
  if (!document.querySelector('script[data-lc="chat"]')) {
    const s = document.createElement("script");
    s.src = "https://widgets.leadconnectorhq.com/loader.js";
    s.setAttribute("data-resources-url", "https://widgets.leadconnectorhq.com/chat-widget/loader.js");
    s.setAttribute("data-widget-id", "6a60ff820c664691c2a9e47d");
    s.setAttribute("data-lc", "chat");
    document.body.appendChild(s);
  }
};

/** Force-activate immediately (e.g. when the user opens the quote-form modal). */
export const activateThirdParty = () => {
  if (activated) return;
  activated = true;
  loadEmbedScripts();
  notify();
};

/** Attach the one-time interaction listeners. Safe to call more than once. */
export const armThirdPartyGate = () => {
  if (armed || activated || typeof window === "undefined") return;
  armed = true;
  const handler = () => {
    INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, handler));
    activateThirdParty();
  };
  INTERACTION_EVENTS.forEach((e) => window.addEventListener(e, handler, { passive: true }));
};

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

/** React hook — re-renders the component once third-party embeds are activated. */
export const useThirdPartyActive = () =>
  useSyncExternalStore(subscribe, () => activated, () => false);
